import { ReactNode } from 'react';

import { UIProvider, useUI } from '@v2/shared/lib/ui';
import { useCart } from '@v2/shared/lib/cart';
import { PaymentConfig } from '@v2/shared/lib/types';
import {
  isMobileViewport,
  SaleProvider,
  useSale,
  useSaleSearch,
  useSaleSearchIndex,
} from '@v2/shared/lib/megaSale';
import { trackAddToCart } from '@v2/shared/lib/analytics';

import CartDrawer from './CartDrawer/CartDrawer';
import { SaleSearchProps } from './types';

export type MegaSaleLayoutProps = {
  /** The sale's name — used for the drawer and menu headings, not drawn as a heading. */
  title?: string;
  /**
   * The sale document's full url, decided on the server (SaleDocumentUrl): the CDN on
   * DiscountMags in production, the site's own origin everywhere else.
   */
  saleDocumentUrl: string;
  previewMode?: boolean;
  /**
   * The sale has ended, or has not started yet.
   *
   * A layout prop, and not the page's copy of the same fact, because it is the layout
   * that holds the sale: it decides whether the document is fetched at all, and its
   * header's category menu and scoped search are the sale. What the page says about the
   * dates — the wording, the end date, the countdown — stays on the page's own props.
   *
   * Safe in a bag that section.php may cache for half an hour, because it only ever
   * caches a sale that is live: the condition around `$staticSectionCacheKey` requires
   * `active == "Y"`, `expired == "N"` and no `xEO`, so a cached bag cannot say `true`,
   * and a visitor for whom it *is* true never reaches the cache.
   */
  saleOver?: boolean;
  initialCategoryId?: number | null;
  initialProductId?: number | null;
  /**
   * Braintree's settings, for the wallet buttons in the cart drawer. A layout prop rather
   * than a page one because the drawer is the layout's — and it is safe to cache here
   * only because this layout's pages do not server-render; see the note on
   * `ProductPagePropsBuilder::buildPaymentConfig`, which says the same thing.
   */
  payment?: PaymentConfig | null;
  children?: ReactNode;
};

type Props = MegaSaleLayoutProps & {
  /**
   * The header, drawn with the sale's search box already wired.
   *
   * A render prop rather than a node because the search has to be built *inside* the
   * provider — it is this sale's index, not the site's — and the header is the only
   * thing that draws it. Every brand hands over something different here: the
   * DiscountMags sale keeps that brand's fixed header, and MagSpot, MagZoom and TopMags
   * share `Navigation` beside this file.
   */
  navigation: (searchProps: SaleSearchProps) => ReactNode;
  /**
   * The brand's footer.
   *
   * Passed in rather than drawn here because there is no shared one: each brand's
   * `Layouts/Full/Footer` is its own markup, its own link list and its own seals, and
   * nothing under `shared/` may reach into a brand folder to pick one.
   */
  footer?: ReactNode;
};

/**
 * The chrome of a sale page, less the header and the footer.
 *
 * Full's layout with the categories taken out and the cart moved into a drawer. It is a
 * layout of its own rather than a flag on Full because the two differ in what they *own*
 * and not only in what they draw: this one holds the sale, because the header needs it
 * (the mobile menu lists this sale's sections, and the search box searches its products)
 * and so does the page. tree.tsx composes layout-around-page, so the layout is the only
 * place one copy of the sale can serve both.
 *
 * That is also why the provider is here and the consumer is split in two: `SaleProvider`
 * has to be above the header, so the header lives in an inner component that can call
 * `useSale`.
 *
 * Shared, and a shell rather than the layout itself, because the two things a sale page
 * cannot say for itself are the two a brand owns — its mark and its footer. Everything
 * else on the page is the same sale drawn the same way, so it is here once: the providers,
 * the cart drawer, the gift upsell, the floor under the content. A brand's
 * `Layouts/MegaSale` is what names this and fills the two slots.
 */
export default function SaleShell({
  saleDocumentUrl,
  previewMode = false,
  saleOver = false,
  initialCategoryId = null,
  initialProductId = null,
  payment = null,
  navigation,
  footer,
  children,
}: Props) {
  return (
    /*
     * UIProvider around the whole layout, and not around the header alone.
     *
     * Full mounts it inside `NavigationMount`, so on that layout a page sits outside it
     * and reads `headerHeight: 0` back — which is why `useHeaderHeight` exists to measure
     * the header a second time from the DOM. This page cannot use that workaround,
     * because it is not only the page that needs the value: the header itself has to be
     * measured, and `headerRef` comes from the same context. Left outside, `useUI()`
     * returned the module's default object, `headerRef` was `undefined`, and the header
     * was never measured at all — the sticky headings then fell back to a hard-coded 82px
     * and hung 29px below a 53px mobile header, and the mobile search toggle was a no-op
     * because the default `searchBar` actions do nothing.
     *
     * One provider above both is the fix and it is also the arrangement Full should have
     * had; moving Full's is a separate change with its own blast radius.
     */
    <UIProvider>
      <SaleProvider
        saleDocumentUrl={saleDocumentUrl}
        previewMode={previewMode}
        saleOver={saleOver}
        initialCategoryId={initialCategoryId}
        initialProductId={initialProductId}
      >
        <Chrome payment={payment} navigation={navigation} footer={footer}>
          {children}
        </Chrome>
      </SaleProvider>
    </UIProvider>
  );
}

/**
 * Everything that needs the sale in hand.
 *
 * The restrictions are the page's, not the layout's — they are the visitor's and must
 * not reach a cached render — so the search index is built without them here and the
 * page filters what it draws. The one thing that would be wrong is a *suggestion* for a
 * product this visitor may not see, so the page publishes its restrictions back through
 * the context for the index to honour. Until it has, the index is empty, which is the
 * safe direction to fail.
 */
function Chrome({
  payment,
  navigation,
  footer,
  children,
}: {
  payment?: PaymentConfig | null;
  navigation: (searchProps: SaleSearchProps) => ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  const { data, select, giftOffer, cartDrawer, restrictions } = useSale();
  const { searchBar } = useUI();
  const entries = useSaleSearchIndex(data, restrictions);
  /*
   * Choosing a suggestion scrolls to the product, every time it is chosen, and on a
   * desktop opens its detail view when it gets there.
   *
   * Both halves are the legacy page's behaviour and neither is arbitrary. The scroll is
   * the whole point of the search — a sale is one long page and the search is how you get
   * down it — and the dialog is what a desktop reader wants next, having just picked a
   * magazine out of a list of ten. A phone gets the scroll alone: it has just been taken
   * to the product, and covering it with a full-screen dialog of the same product hides
   * the thing it was asked to show.
   *
   * Asked at the moment of the click rather than watched, so a customer who rotates their
   * phone gets the rule for the width they are actually on.
   */
  const searchProps = useSaleSearch(entries, (categoryId, productId) => {
    select(categoryId, productId, { openDetail: !isMobileViewport() });

    /*
     * And let go of the search box.
     *
     * On every other page a chosen suggestion is a navigation, so nobody noticed that the
     * input keeps focus after one: the page goes away. Here it does not, and a focused
     * search box on a phone means the dark overlay behind the suggestions stays up and the
     * keyboard stays open — so the sale scrolls perfectly to a product the customer then
     * cannot see. The legacy page blurred the field for the same reason.
     *
     * `useAutoComplete` tries to do this itself and cannot: its `inputRef` is returned in
     * `inputProps`, which `SearchForm` does not spread, so its `blur()` is called on null.
     * Fixing that is a change to the site-wide search box and belongs on its own.
     */
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    searchBar.unfocus();
  });
  const { addItem, updateItem } = useCart();

  const addGift = async (skuCode: string, offerCode: string | null) => {
    // The same sku a second time, then flagged as a gift. The add endpoint takes no gift
    // flag, so it is two calls — which is what the legacy form did as well, by selecting
    // its synthetic "Gift" option before submitting.
    const cart = await addItem(skuCode, offerCode);
    const added = cart.items?.[cart.items.length - 1];

    if (added) {
      // Reported on the add and not after the gift flag, which is a property of a line
      // that is already in the cart: the legacy form set its "Gift" option before
      // submitting and so sent one add_to_cart for the same thing.
      trackAddToCart(added);
      await updateItem(added.id, { sendAsGift: true });
    }

    giftOffer.dismiss();
    cartDrawer.open();
  };

  return (
    <>
      {navigation(searchProps)}
      {/*
        A floor under the content, so the footer is never pulled up against it.

        A sale that has ended draws its artwork and nothing else — a banner is a few
        hundred pixels — and the footer landed immediately underneath, which reads as a
        page that failed to load rather than one with nothing left to sell. A running
        sale is far taller than this and never reaches the floor, so it is whitespace
        only where whitespace is the missing thing.

        Proportional rather than a pixel count because the gap it fills is the rest of
        the screen: the same rule wants ~500px under the banner on a 1440x900 desktop
        and ~500px on a 390x844 phone, and one `vh` value says that at both.
      */}
      <main id="main" role="main" className="min-h-[60vh]">
        {children}
      </main>
      <CartDrawer onAddGift={addGift} payment={payment} />
      {footer}
    </>
  );
}
